import { ArrowUpRight, ChevronRight, FileUp, Gauge } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Reveal } from "@/components/Motion";
import { API, PORTAL_URL, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";
import { TUNING_CATEGORIES } from "@/lib/tuningCategories";
import { EstimatedBadge } from "@/pages/TuningCategoryIndex";

export default function TuningCategoryMake({ category }) {
  const cat = TUNING_CATEGORIES[category];
  const { make } = useParams();
  const [data, setData] = useState(null);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    setData(null);
    setMiss(false);
    axios
      .get(`${API}/tuning-database/categories/${category}/makes/${make}`)
      .then((r) => setData(r.data))
      .catch(() => setMiss(true));
  }, [category, make]);

  // ---- SEO head ----
  useEffect(() => {
    if (!data) return;
    document.title = cat.makeTitle(data.make);
    document.querySelector('meta[name="description"]')?.setAttribute("content", cat.makeDesc(data.make, data.vehicle_count, data.model_count));
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}`;
    setJsonLd("tcm-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
        { "@type": "ListItem", position: 3, name: cat.name, item: `${SITE_ORIGIN}${cat.basePath}` },
        { "@type": "ListItem", position: 4, name: data.make, item: `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}` },
      ],
    });
    setJsonLd("tcm-collection", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: cat.makeH1(data.make),
      url: `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}`,
      isPartOf: { "@type": "WebSite", name: "Regal Tuning", url: `${SITE_ORIGIN}/` },
      about: { "@type": "Brand", name: data.make },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: data.model_count,
        itemListElement: data.models.slice(0, 60).map((m, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${data.make} ${m.model}`,
          url: `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}/${m.model_slug}`,
        })),
      },
    });
    return () => {
      removeJsonLd("tcm-breadcrumb");
      removeJsonLd("tcm-collection");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (miss) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="tcm-notfound">
        <div className="container-regal">
          <div className="border border-line bg-surface p-10 text-center">
            <Gauge size={28} className="mx-auto text-regal-amber" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Manufacturer not found</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">We don't have {cat.name.toLowerCase()} tuning coverage for that manufacturer yet — coverage grows continuously.</p>
            <Link to={cat.basePath} className="mt-6 inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="tcm-notfound-back">
              All {cat.name} Manufacturers
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="tcm-loading">
        <div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div>
      </main>
    );
  }

  return (
    <main className="pt-32 sm:pt-40 pb-24" data-testid="tcm-page">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-database" className="hover:text-regal-amber">Tuning Database</Link>
          <ChevronRight size={12} />
          <Link to={cat.basePath} className="hover:text-regal-amber" data-testid="tcm-breadcrumb-cat">{cat.name}</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{data.make}</span>
        </nav>

        <p className="overline-label mb-4">{cat.name} Manufacturer</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="tcm-h1">
          {category === "trucks" ? (
            <>{data.make} <span className="text-regal-amber">Truck</span> Tuning &amp; ECU Remapping</>
          ) : (
            <>{data.make} <span className="text-regal-amber">Tuning</span> &amp; ECU Remapping</>
          )}
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="tcm-intro">
          {cat.makeDesc(data.make, data.vehicle_count, data.model_count)}
        </p>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="tcm-stats">
          <span className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid="tcm-stat-models">{data.model_count} models</span>
          <span className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid="tcm-stat-vehicles">{data.vehicle_count} variants with published figures</span>
          {category === "agriculture" && data.estimated_count > 0 && (
            <span className="border border-amber-300 bg-amber-50 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-amber-700" data-testid="tcm-stat-estimated">
              {data.estimated_count} with estimated Stage 1
            </span>
          )}
          {(data.fuels || []).map((f) => (
            <span key={f} className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500">{f}</span>
          ))}
        </div>

        {/* MODEL INDEX */}
        <div className="mt-12" data-testid="tcm-models">
          <p className="overline-label mb-6">{`Choose your ${data.make} model`}</p>
          <div className="space-y-8">
            {data.models.map((m, mi) => (
              <Reveal key={m.model_slug} delay={Math.min(mi * 0.03, 0.3)}>
                <section className="border border-line bg-white p-6 sm:p-8" data-testid={`tcm-model-${m.model_slug}`}>
                  <h2 className="font-display text-lg sm:text-xl font-bold uppercase tracking-wide text-ink">
                    <Link to={`${cat.basePath}/${data.make_slug}/${m.model_slug}`} className="hover:text-regal-amber transition-colors" data-testid={`tcm-model-link-${m.model_slug}`}>
                      {data.make} <span className="text-regal-amber">{m.model}</span>
                    </Link>
                    <span className="ml-3 font-mono text-[10px] font-normal uppercase tracking-[0.16em] text-neutral-400">{m.variants.length} variant{m.variants.length === 1 ? "" : "s"}</span>
                  </h2>
                  <ul className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                    {m.variants.map((v) => (
                      <li key={v.url}>
                        <Link to={v.url} className="group inline-flex items-baseline gap-2 text-sm text-neutral-500 hover:text-regal-amber transition-colors" data-testid={`tcm-variant-${v.engine.replace(/\W+/g, "-").toLowerCase()}`}>
                          <ArrowUpRight size={12} className="shrink-0 translate-y-px text-regal-amber/60 group-hover:text-regal-amber" />
                          <span>
                            {v.label}
                            {v.original_bhp != null && v.stage1_bhp != null && (
                              <span className="ml-2 font-mono text-[10px] text-neutral-400">
                                {v.original_bhp} → <span className="font-bold text-regal-amber">{v.stage1_bhp}</span> bhp
                                {v.data_status === "estimated" && <EstimatedBadge testId={`tcm-est-${v.engine.replace(/\W+/g, "-").toLowerCase()}`} />}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-line bg-surface p-8 sm:flex-row sm:items-center" data-testid="tcm-cta">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-ink">Get your {data.make} remapped</h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-400">Request a quote with your {cat.vehicleNoun} details and our calibration team will confirm support and expected gains in writing before any work begins.</p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors" data-testid="tcm-cta-portal">
              <FileUp size={15} /> Upload a File
            </a>
            <Link to={`/contact?subject=${encodeURIComponent(`Remap quote — ${data.make} ${cat.vehicleNoun}`)}`} className="inline-flex items-center justify-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="tcm-cta-quote">
              Request a Quote <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
